/**
 * DOMI-CARE - Módulo: Epicrisis e Informe Clínico Domiciliario Oficial (PDF A4)
 * Genera el documento médico asistencial para presentación a Obras Sociales y Auditoría Médica.
 */

import { offlineStore } from '../../api/offlineStore.js';
import { licenseState } from '../../state/licenseState.js';

function getSafeSignatureSrc(src) {
  if (!src) return '';
  if (typeof src === 'string' && src.startsWith('data:image/svg+xml;utf8,<')) {
    const rawSvg = src.replace('data:image/svg+xml;utf8,', '');
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(rawSvg);
  }
  return src;
}

export async function renderReportesView(container, activePatientId) {
  const paciente = await offlineStore.getById('pacientes', activePatientId);
  if (!paciente) {
    container.innerHTML = `<div class="card" style="padding: 2rem; text-align: center;">Por favor selecciona un paciente en el padrón.</div>`;
    return;
  }

  const allEvoluciones = await offlineStore.getAll('evoluciones');
  const evoluciones = allEvoluciones.filter(e => e.patientId === activePatientId)
    .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));

  const allSignos = await offlineStore.getAll('signos');
  const signos = allSignos.filter(s => s.patientId === activePatientId)
    .sort((a, b) => new Date(b.fechaHora) - new Date(a.fechaHora));
  const latestSignos = signos[0] || null;

  const allEscalas = await offlineStore.getAll('escalas');
  const escalas = allEscalas.filter(e => e.patientId === activePatientId)
    .sort((a, b) => new Date(b.fechaHora) - new Date(a.fechaHora));

  const licInfo = licenseState.getLicenseInfo();
  const nombreInstitucion = licInfo.companyName || 'DOMI-CARE | SISTEMA DE INTERNACIÓN DOMICILIARIA';

  container.innerHTML = `
    <!-- Barra de Control Web (Oculta en Impresión) -->
    <div class="view-header">
      <div>
        <h1 class="view-title">Epicrisis e Informe Clínico Domiciliario</h1>
        <p class="view-subtitle">Documento médico oficial para presentación ante Obras Sociales, Prepagas y Auditoría Médica</p>
      </div>
      <div class="view-actions">
        <button id="btn-imprimir-hcd" class="btn btn-primary" style="padding: 0.65rem 1.4rem; font-size: 0.95rem;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 0.35rem;"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
          🖨️ Imprimir / Guardar en PDF (A4)
        </button>
      </div>
    </div>

    <!-- DOCUMENTO MÉDICO OFICIAL IMPRIMIBLE (TAMAÑO A4) -->
    <div class="card" id="printable-hcd" style="margin-bottom: 2rem; border: 1px solid var(--border-medium); background: #ffffff; color: #0f172a; padding: 2rem; max-width: 900px; margin-left: auto; margin-right: auto; box-shadow: var(--shadow-md);">
      
      <!-- MEMBRETE INSTITUCIONAL -->
      <div style="border-bottom: 2px solid #0284c7; padding-bottom: 1rem; margin-bottom: 1.5rem; display: flex; justify-content: space-between; align-items: flex-start;">
        <div>
          <div style="font-size: 1.35rem; font-weight: 800; color: #0369a1; letter-spacing: -0.01em;">
            ${nombreInstitucion.toUpperCase()}
          </div>
          <div style="font-size: 0.85rem; font-weight: 700; color: #475569; text-transform: uppercase; margin-top: 0.15rem;">
            SERVICIO DE INTERNACIÓN Y ASISTENCIA DOMICILIARIA INTERDISCIPLINARIA
          </div>
          <div style="font-size: 0.75rem; color: #64748b; margin-top: 0.1rem;">
            Habilitación Sanitaria Ministerial • Coordinación Médica Permanente
          </div>
        </div>

        <div style="text-align: right; font-size: 0.8rem; background: #f8fafc; padding: 0.6rem 0.9rem; border-radius: 6px; border: 1px solid #e2e8f0;">
          <div><strong>FECHA DE EMISIÓN:</strong> ${new Date().toLocaleDateString('es-AR')}</div>
          <div><strong>EXPEDIENTE:</strong> ${paciente.id}</div>
          <div><strong>ESTADO:</strong> <span style="color: #16a34a; font-weight: 700;">INTERNACIÓN ACTIVA</span></div>
        </div>
      </div>

      <!-- TÍTULO DEL DOCUMENTO -->
      <div style="text-align: center; margin-bottom: 1.5rem;">
        <h2 style="font-size: 1.15rem; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.04em;">
          EPICRISIS Y RESUMEN DE HISTORIA CLÍNICA DOMICILIARIA
        </h2>
        <div style="font-size: 0.8rem; color: #475569;">
          INFORME DE CONTINUIDAD ASISTENCIAL Y PLAN DE ATENCIÓN MÉDICA (PAM)
        </div>
      </div>

      <!-- 1. DATOS DE FILIACIÓN Y COBERTURA -->
      <div class="print-avoid-break" style="margin-bottom: 1.25rem;">
        <div style="background: #f1f5f9; padding: 0.4rem 0.75rem; font-weight: 800; font-size: 0.85rem; color: #1e293b; border-left: 4px solid #0284c7; text-transform: uppercase; margin-bottom: 0.6rem;">
          1. Datos de Filiación del Paciente y Cobertura Médica
        </div>

        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; font-size: 0.825rem; background: #fafafa; padding: 0.85rem; border-radius: 6px; border: 1px solid #e2e8f0;">
          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">Paciente:</span>
            <div style="font-weight: 700; font-size: 0.95rem; color: #0f172a;">${paciente.nombreCompleto}</div>
          </div>
          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">DNI / Documento:</span>
            <div style="font-weight: 700; font-size: 0.95rem;">${paciente.dni}</div>
          </div>
          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">Edad / Género:</span>
            <div style="font-weight: 600;">${paciente.edad || 70} años (${paciente.genero})</div>
          </div>

          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">Domicilio:</span>
            <div style="font-weight: 600;">${paciente.direccion} (${paciente.localidad})</div>
          </div>
          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">Cuidador Principal:</span>
            <div style="font-weight: 600;">${paciente.accesibilidad?.cuidadorNombre || 'Familiar a cargo'} (${paciente.accesibilidad?.cuidadorVinculo || 'Familiar'})</div>
          </div>
          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">Teléfono de Contacto:</span>
            <div style="font-weight: 600;">${paciente.accesibilidad?.cuidadorTelefono || paciente.telefono}</div>
          </div>

          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">Obra Social / Prepaga:</span>
            <div style="font-weight: 700; color: #0369a1;">${paciente.obraSocial}</div>
          </div>
          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">N° Afiliado / Plan:</span>
            <div style="font-weight: 600;">${paciente.numeroAfiliado} (${paciente.planCobertura || 'Plan Regular'})</div>
          </div>
          <div>
            <span style="color: #64748b; font-size: 0.72rem; text-transform: uppercase;">Nivel de Complejidad:</span>
            <div style="font-weight: 700; color: #b91c1c;">${paciente.nivelComplejidad}</div>
          </div>
        </div>
      </div>

      <!-- 2. DIAGNÓSTICO CLÍNICO SEGÚN CIE-10 -->
      <div class="print-avoid-break" style="margin-bottom: 1.25rem;">
        <div style="background: #f1f5f9; padding: 0.4rem 0.75rem; font-weight: 800; font-size: 0.85rem; color: #1e293b; border-left: 4px solid #0284c7; text-transform: uppercase; margin-bottom: 0.6rem;">
          2. Diagnóstico Clínico (Clasificación Internacional CIE-10)
        </div>

        <div style="padding: 0.75rem; background: #fafafa; border-radius: 6px; border: 1px solid #e2e8f0; font-size: 0.85rem;">
          <div style="margin-bottom: 0.4rem;">
            <strong style="color: #0369a1;">Diagnóstico Principal: </strong>
            <span style="font-weight: 700;">[${paciente.diagnosticoPrincipal?.code}] ${paciente.diagnosticoPrincipal?.desc}</span>
          </div>
          ${paciente.diagnosticosSecundarios && paciente.diagnosticosSecundarios.length > 0 ? `
            <div>
              <strong style="color: #475569;">Comorbilidades / Diagnósticos Secundarios: </strong>
              <span>${paciente.diagnosticosSecundarios.map(d => `[${d.code}] ${d.desc}`).join(' • ')}</span>
            </div>
          ` : ''}
        </div>
      </div>

      <!-- 3. PLAN DE ATENCIÓN MÉDICA (PAM) -->
      <div class="print-avoid-break" style="margin-bottom: 1.25rem;">
        <div style="background: #f1f5f9; padding: 0.4rem 0.75rem; font-weight: 800; font-size: 0.85rem; color: #1e293b; border-left: 4px solid #0284c7; text-transform: uppercase; margin-bottom: 0.6rem;">
          3. Plan de Atención Médica Interdisciplinario (PAM)
        </div>

        <div style="padding: 0.75rem; background: #fafafa; border-radius: 6px; border: 1px solid #e2e8f0; font-size: 0.825rem; display: flex; flex-direction: column; gap: 0.4rem;">
          <div>
            <strong>Objetivos Terapéuticos: </strong>
            <span>${paciente.pam?.objetivos || 'Estabilización clínica y rehabilitación funcional en domicilio.'}</span>
          </div>
          <div>
            <strong>Frecuencia de Prestaciones Prescriptas: </strong>
            <span style="color: #0369a1; font-weight: 600;">${paciente.pam?.frecuenciaVisitas || 'Visita médica periódica, enfermería y kinesiología.'}</span>
          </div>
        </div>
      </div>

      <!-- 4. VALORACIÓN CLÍNICA ESTANDARIZADA (ESCALAS Y CONSTANTES) -->
      <div class="print-avoid-break" style="margin-bottom: 1.25rem;">
        <div style="background: #f1f5f9; padding: 0.4rem 0.75rem; font-weight: 800; font-size: 0.85rem; color: #1e293b; border-left: 4px solid #0284c7; text-transform: uppercase; margin-bottom: 0.6rem;">
          4. Valoración Funcional, Escalas Geriátricas y Signos Vitales
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; font-size: 0.8rem;">
          <!-- Escalas -->
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.75rem; background: #fafafa;">
            <div style="font-weight: 700; margin-bottom: 0.4rem; color: #1e293b;">Escalas Estandarizadas de Valoración:</div>
            ${escalas.length > 0 ? escalas.map(e => `
              <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed #cbd5e1; padding: 0.25rem 0;">
                <span><strong>${e.tipo}:</strong> ${e.puntaje} pts</span>
                <span style="font-weight: 600; color: #334155;">${e.clasificacion}</span>
              </div>
            `).join('') : '<div style="color: #64748b;">Sin escalas registradas.</div>'}
          </div>

          <!-- Constantes Vitales -->
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.75rem; background: #fafafa;">
            <div style="font-weight: 700; margin-bottom: 0.4rem; color: #1e293b;">Último Control de Signos Vitales:</div>
            ${latestSignos ? `
              <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.4rem; text-align: center;">
                <div style="background: #ffffff; padding: 0.3rem; border: 1px solid #e2e8f0; border-radius: 4px;">
                  <div style="font-size: 0.7rem; color: #64748b;">Tensión Art.</div>
                  <div style="font-weight: 700;">${latestSignos.sistolica || '--'}/${latestSignos.diastolica || '--'}</div>
                </div>
                <div style="background: #ffffff; padding: 0.3rem; border: 1px solid #e2e8f0; border-radius: 4px;">
                  <div style="font-size: 0.7rem; color: #64748b;">FC (lpm)</div>
                  <div style="font-weight: 700;">${latestSignos.frecuenciaCardiaca || '--'}</div>
                </div>
                <div style="background: #ffffff; padding: 0.3rem; border: 1px solid #e2e8f0; border-radius: 4px;">
                  <div style="font-size: 0.7rem; color: #64748b;">SatO2</div>
                  <div style="font-weight: 700;">${latestSignos.saturacionO2 || '--'}%</div>
                </div>
                <div style="background: #ffffff; padding: 0.3rem; border: 1px solid #e2e8f0; border-radius: 4px;">
                  <div style="font-size: 0.7rem; color: #64748b;">Temp °C</div>
                  <div style="font-weight: 700;">${latestSignos.temperatura || '--'}°</div>
                </div>
                <div style="background: #ffffff; padding: 0.3rem; border: 1px solid #e2e8f0; border-radius: 4px;">
                  <div style="font-size: 0.7rem; color: #64748b;">Glucemia</div>
                  <div style="font-weight: 700;">${latestSignos.glucemia || '--'}</div>
                </div>
                <div style="background: #ffffff; padding: 0.3rem; border: 1px solid #e2e8f0; border-radius: 4px;">
                  <div style="font-size: 0.7rem; color: #64748b;">Dolor EVA</div>
                  <div style="font-weight: 700;">${latestSignos.dolorEVA !== undefined ? latestSignos.dolorEVA : '--'}/10</div>
                </div>
              </div>
            ` : '<div style="color: #64748b;">Sin registros de signos vitales.</div>'}
          </div>
        </div>
      </div>

      <!-- 5. REGISTRO CRONOLÓGICO DE EVOLUCIONES EN DOMICILIO -->
      <div class="print-avoid-break" style="margin-bottom: 1.5rem;">
        <div style="background: #f1f5f9; padding: 0.4rem 0.75rem; font-weight: 800; font-size: 0.85rem; color: #1e293b; border-left: 4px solid #0284c7; text-transform: uppercase; margin-bottom: 0.6rem;">
          5. Registro Cronológico de Prestaciones y Evoluciones (${evoluciones.length})
        </div>

        <div style="display: flex; flex-direction: column; gap: 0.85rem;">
          ${evoluciones.map(evo => {
            const sigProf = getSafeSignatureSrc(evo.firmas?.profesional);
            const sigFam = getSafeSignatureSrc(evo.firmas?.familiar);

            return `
              <div class="print-avoid-break" style="border: 1px solid #cbd5e1; border-radius: 6px; padding: 0.75rem; background: #ffffff; font-size: 0.825rem;">
                <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.35rem; margin-bottom: 0.4rem;">
                  <div>
                    <strong style="color: #0369a1;">${evo.fechaHora}</strong> — 
                    <strong>${evo.disciplina}:</strong> ${evo.prestadorNombre} (Mat. ${evo.matricula || 'MN'})
                  </div>
                  <div style="color: #64748b; font-size: 0.75rem;">Checkin: ${evo.gps || 'Domicilio'}</div>
                </div>

                <div style="margin-bottom: 0.35rem;">
                  <strong>Evolución / Examen: </strong>${evo.subjetivoObjetivo}
                </div>
                <div style="margin-bottom: 0.35rem;">
                  <strong>Diagnóstico de Visita: </strong><span style="font-weight: 600;">${evo.evaluacionDiagnostica}</span>
                </div>
                <div style="margin-bottom: 0.4rem;">
                  <strong>Procedimientos / Prácticas: </strong>${evo.practicasRealizadas || evo.planTratamiento}
                </div>

                <!-- Firmas Digitales de la Visita -->
                <div style="display: flex; gap: 1rem; border-top: 1px dashed #e2e8f0; padding-top: 0.4rem; margin-top: 0.4rem;">
                  <div style="flex: 1; text-align: center;">
                    <div style="font-size: 0.68rem; color: #64748b; text-transform: uppercase;">Firma del Profesional</div>
                    ${sigProf ? `
                      <img src="${sigProf}" alt="Firma Profesional" style="max-height: 40px; margin: 0 auto; display: block;">
                    ` : '<div style="font-size: 0.72rem; color: #94a3b8;">Firma Digital Registrada</div>'}
                    <div style="font-size: 0.72rem; font-weight: 600;">${evo.prestadorNombre}</div>
                  </div>

                  <div style="flex: 1; text-align: center;">
                    <div style="font-size: 0.68rem; color: #64748b; text-transform: uppercase;">Conformidad Cuidador / Familiar</div>
                    ${sigFam ? `
                      <img src="${sigFam}" alt="Firma Familiar" style="max-height: 40px; margin: 0 auto; display: block;">
                    ` : '<div style="font-size: 0.72rem; color: #94a3b8;">Conformidad Registrada</div>'}
                    <div style="font-size: 0.72rem; font-weight: 600;">${evo.firmas?.aclaracionFamiliar || 'Familiar Responsable'}</div>
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>

      <!-- 6. CIERRE MÉDICO LEGAL Y SELLOS PROFESIONALES -->
      <div class="print-avoid-break" style="border-top: 2px solid #0f172a; padding-top: 1.5rem; margin-top: 2rem; display: flex; justify-content: space-around; align-items: flex-end; text-align: center; font-size: 0.8rem;">
        <div style="width: 240px; border-top: 1px solid #000000; padding-top: 0.4rem;">
          <div style="font-weight: 700;">Médico Coordinador / Tratante</div>
          <div style="font-size: 0.72rem; color: #64748b;">Firma y Sello Aclaratorio</div>
        </div>

        <div style="width: 240px; border-top: 1px solid #000000; padding-top: 0.4rem;">
          <div style="font-weight: 700;">Dirección Médica Institucional</div>
          <div style="font-size: 0.72rem; color: #64748b;">${nombreInstitucion}</div>
        </div>
      </div>

    </div>
  `;

  // Listener para botón de impresión nativa (A4)
  const btnPrint = document.getElementById('btn-imprimir-hcd');
  if (btnPrint) {
    btnPrint.addEventListener('click', () => {
      window.print();
    });
  }
}
